import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {FormGroup} from "@angular/forms";
import {EntityData, EntityDataStatus, EntityModel, EntityModelStatus} from "@fedeltapointofsale/data-client";
import {debounceTime, Subject, Subscription} from "rxjs";
import {PriceLevelSearchModel} from "../../../pricing/price-level/price-level-search.model";
import {AbstractSellableData, ProductPriceLevelData} from "../sellable.data";
import {StockCostingType} from "../../../stock/costing/stock-costing-type";
import {EditService} from "../../../framework/entity/edit/edit-page/edit.service";
import {StockCostingService} from "../../../stock/costing/stock-costing.service";
import {
    CostedCompositeComponent,
    CostedInventoryQuantity,
    PriceLevelPricing,
    StockCosting
} from "../../../stock/costing/stock-costing.data";
import {ProductLineData, ProductLineItemData} from "../../product-line/product-line.data";
import {
    EntityFormCollectionControl
} from "../../../framework/entity/edit/entity-form-builder/entity-form-collection-control";
import {CompositeProductComponentData, CompositeProductData} from "../../composite-product/composite-product.data";
import {TaxReferenceData} from "../../../system/tax/tax.data";
import {Config} from "../../../config";
import {EditMode} from "../../../framework/entity/edit/edit-page/edit-mode";

@Component({
    selector: "wbo-sellable-edit-price-levels",
    templateUrl: "./sellable-edit-price-levels.component.html",
    providers: [PriceLevelSearchModel]
})
export class SellableEditPriceLevelsComponent implements OnInit, OnDestroy {
    Config = Config;
    EditMode = EditMode;

    productPriceLevelData: ProductPriceLevelData[];
    costingType: StockCostingType;
    customCost: number;
    costingOutletId: number;
    private subscriptions: Subscription = new Subscription();
    private loadedSubscription: Subscription;
    private baseDetailsSubject: Subject<void> = new Subject<void>();

    @Input() showScaleFields: boolean;

    constructor(public model: EntityModel<AbstractSellableData<any>>,
                private priceLevelSearchModel: PriceLevelSearchModel,
                private stockCostingService: StockCostingService,
                public editService: EditService) {
        priceLevelSearchModel.setFilterValue("restrict", 1);
        this.subscriptions.add(this.baseDetailsSubject.pipe(debounceTime(1000)).subscribe(() => this.calculatePricing()));
    }

    ngOnInit(): void {
        this.subscriptions.add(this.priceLevelSearchModel.search().subscribe(() => {
            this.subscriptions.add(this.model.$data.subscribe(() => {
                if (this.loadedSubscription) {
                    this.loadedSubscription.unsubscribe();
                }
                if (this.model.status === EntityModelStatus.INSERT) {
                    this.loadPriceLevels();
                }
                this.loadedSubscription = this.model.$collectionLoaded("priceLevels").pipe(debounceTime(500)).subscribe(() => {
                    this.loadPriceLevels();
                    this.calculatePricing();
                });
                const forceLoad: ProductPriceLevelData[] = this.model.data.priceLevels;
            }, () => {
                // @todo should be dealt with
            }));
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        if (this.loadedSubscription) {
            this.loadedSubscription.unsubscribe();
        }
    }

    loadPriceLevels(): void {
        this.productPriceLevelData = [];
        this.priceLevelSearchModel.data.forEach((priceLevel: any) => {
            let productPriceLevel: ProductPriceLevelData = this.model.data.priceLevels.find((ppl: ProductPriceLevelData) => ppl.priceLevel.priceLevelId === priceLevel.priceLevelId);
            if (!productPriceLevel) {
                productPriceLevel = <ProductPriceLevelData>EntityData.create(ProductPriceLevelData);
                productPriceLevel.priceLevel.assign(priceLevel, EntityDataStatus.LOADED);
            }
            this.productPriceLevelData.push(productPriceLevel);
        });
    }

    saveHandler(data: ProductPriceLevelData): void {
        const index = this.model.data.priceLevels.findIndex((value) => value.priceLevel.priceLevelId === data.priceLevel.priceLevelId);
        if (data.markup.type == undefined) {
            if (index !== -1) {
                this.model.data.priceLevels.splice(index, 1);
                data.markup.value = undefined;
            }
        } else {
            if (!data.markup.value) {
                data.markup.value = 0;
            }
            if (index === -1) {
                this.model.data.priceLevels.push(data);
            }
        }

        this.calculatePricing();
    }

    markupTypeChanged({value, dataItem, formGroup}: { value: any, dataItem: any, formGroup: FormGroup }): void {
        if (value === null) {
            formGroup.get("markup.value").reset();
        }

        // Live update of data item to allow notation on amount column to change
        dataItem.markup.type = value;

        this.calculatePricing();
    }

    baseDetailsChanged() {
        this.baseDetailsSubject.next();
    }

    public calculatePricing(): void {
        if (!this.productPriceLevelData) {
            return;
        }

        const stockCosting: StockCosting = <StockCosting>EntityData.create(StockCosting);
        stockCosting.costingType = this.costingType;
        stockCosting.totalCost = this.customCost;
        stockCosting.outletId = this.costingOutletId;
        stockCosting.basePrice = this.model.data.price;
        stockCosting.baseCostEx = this.model.data.baseCostEx;
        stockCosting.taxes = this.model.data.taxes.map((tax: TaxReferenceData) => TaxReferenceData.clone(tax));
        stockCosting.pricings = this.productPriceLevelData.map((productPriceLevel: ProductPriceLevelData) => PriceLevelPricing.fromProductPriceLevel(productPriceLevel));
        if (this.model.data instanceof ProductLineData) {
            if (this.model.data.productInventory.items.length > 0 && !this.model.data.productInventory.items.find((productLineItem: ProductLineItemData) => productLineItem[EntityFormCollectionControl.FLAG_CHILD_ERROR])) {
                stockCosting.inventoryQuantities = this.model.data.productInventory.items.map((productLineItem: ProductLineItemData) => CostedInventoryQuantity.fromProductLineItem(productLineItem));
            }
        } else if (this.model.data instanceof CompositeProductData) {
            if (this.model.data.components.length > 0 && !this.model.data.components.find((component: CompositeProductComponentData) => component[EntityFormCollectionControl.FLAG_CHILD_ERROR])) {
                stockCosting.compositeComponents = this.model.data.components.map((component: CompositeProductComponentData) => CostedCompositeComponent.fromCompositeProductComponent(component));
            }
        }

        this.productPriceLevelData.forEach((productPriceLevel: ProductPriceLevelData) => {
            productPriceLevel.priceInc = null;
            productPriceLevel.priceEx = null;
            productPriceLevel.cost = null;
            productPriceLevel.profit = null;
            productPriceLevel.margin = null;
        });

        this.stockCostingService.performAction(stockCosting)
            .then((result: any) => {
                stockCosting.assign(result);
                this.productPriceLevelData.forEach((productPriceLevel: ProductPriceLevelData) => {
                    productPriceLevel.cost = stockCosting.totalCost;
                    stockCosting.pricings.forEach((pricing: PriceLevelPricing) => {
                        if (pricing.priceLevelId === productPriceLevel.priceLevel.priceLevelId) {
                            productPriceLevel.priceInc = pricing.priceInc;
                            productPriceLevel.priceEx = pricing.priceEx;
                            productPriceLevel.profit = pricing.profit;
                            productPriceLevel.margin = pricing.margin;
                        }
                    })
                })
            });
    }

    promptPriceChanged(): void {
        if (this.model.data.promptPrice && this.model.data.promptForDefaultType == undefined) {
            this.model.data.promptForDefaultType = 1;
        }
    }
}
